import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { VoiceSearchProvider } from "@/contexts/VoiceSearchContext";
import { FavouritesProvider } from "@/contexts/FavouritesContext";
import Favourites from "@/pages/Favourites";
import GuestShare from "@/pages/GuestShare";
import VoiceSearchFab from "@/components/VoiceSearchFab";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import { News, NewsPost } from "@/pages/News";
import Directory from "@/pages/Directory";
import Historical from "@/pages/Historical";
import HistoricalRecipe from "@/pages/HistoricalRecipe";
import HistoricalProgress from "@/pages/HistoricalProgress";
import HistoricalRegion from "@/pages/HistoricalRegion";
import Leaderboard from "@/pages/Leaderboard";
import CookProfile from "@/pages/CookProfile";
import RecipeCreator from "@/pages/RecipeCreator";
import TagPage from "@/pages/TagPage";
import CuisinePage from "@/pages/CuisinePage";
import CuisineHub from "@/pages/CuisineHub";
import TagsHub from "@/pages/TagsHub";
import DietIndex from "@/pages/DietIndex";
import DietLanding from "@/pages/DietLanding";
import Shop from "@/pages/Shop";
import HowToUse from "@/pages/HowToUse";
import FAQs from "@/pages/FAQs";
import FAQEntry from "@/pages/FAQEntry";
import Conversions from "@/pages/Conversions";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import OrderHardback from "@/pages/OrderHardback";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import CookbookTrash from "@/pages/CookbookTrash";
import ScanRecipe from "@/pages/ScanRecipe";
import OCRSandbox from "@/pages/OCRSandbox";
import RecipeView from "@/pages/RecipeView";
import RecipeEdit from "@/pages/RecipeEdit";
import CookbookView from "@/pages/CookbookView";
import CookbookEdit from "@/pages/CookbookEdit";
import KitchenView from "@/pages/KitchenView";
import Profile from "@/pages/Profile";
import ActivateFamily from "@/pages/ActivateFamily";
import FamilyEmailInvite from "@/pages/FamilyEmailInvite";
import ShoppingJoin from "@/pages/ShoppingJoin";
import VoicePage from "@/pages/VoicePage";
import Admin from "@/pages/Admin";
import DMCA from "@/pages/DMCA";
import RedeemGift from "@/pages/RedeemGift";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import ShareImport from "@/pages/ShareImport";
import IOSShareSetup from "@/pages/IOSShareSetup";
import Search from "@/pages/Search";
import ShoppingList from "@/pages/ShoppingList";
import ShoppingListHelp from "@/pages/ShoppingListHelp";
import KitchenHelp from "@/pages/KitchenHelp";
import KitchenHelpTerm from "@/pages/KitchenHelpTerm";
import MealPlanner from "@/pages/MealPlanner";
import SharedMealPlan from "@/pages/SharedMealPlan";
import Import from "@/pages/Import";
import ImportLanding from "@/pages/ImportLanding";
import EvernoteImportHelp from "@/pages/EvernoteImportHelp";
import ImportHelpHub from "@/pages/ImportHelpHub";
import ImportHelpApp from "@/pages/ImportHelpApp";
import TryImport from "@/pages/TryImport";
import InstallPrompt from "@/components/InstallPrompt";
import CookieBanner from "@/components/CookieBanner";
import PageMeta from "@/components/PageMeta";
import { SiteFlagsProvider } from "@/contexts/SiteFlagsContext";
import ErrorBoundary from "@/components/ErrorBoundary";

/** /defaultsite — legacy placeholder URL Google indexed before the site
 * relaunched. Even though robots.txt Disallows it, we belt-and-braces the
 * page with an explicit noindex + a canonical pointing at the homepage so
 * Emergent's crawler-cache captures those signals BEFORE the client-side
 * React Router redirects real browsers away.
 */
function DefaultsiteRedirect() {
  return (
    <>
      <PageMeta path="/" title="Recipe Filer" description="" noindex />
      <Navigate to="/" replace />
    </>
  );
}

function AppRouter() {
  const location = useLocation();
  // GA4 SPA page views — one per route change (initial auto page_view is disabled in index.html).
  useEffect(() => {
    if (typeof window.gtag !== "function") return;
    const t = setTimeout(() => {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }, 300);
    return () => clearTimeout(t);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  // Apply favicon override from settings
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`).then((r) => r.json()).then((s) => {
      if (s?.favicon_url) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
        link.href = s.favicon_url;
      }
    }).catch(() => {});
  }, []);
  // Scroll to top on every navigation — desktop AND mobile previously kept the
  // scroll position from the previous page, which on long pages made the new
  // page appear to "open at the bottom". `instant` (vs smooth) makes the jump
  // imperceptible — feels native.
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [location.pathname]);
  // Detect Emergent OAuth callback in URL hash synchronously (avoids race condition)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <>
    <Routes>
      {/* Public + layouted */}
      <Route path="/" element={<Layout><Home /></Layout>} />
      {/* /defaultsite — legacy placeholder URL Cloudflare / Emergent used to
          serve during onboarding. Google indexed it. Redirect to home and let
          robots.txt Disallow finish removing it from search. */}
      <Route path="/defaultsite" element={<DefaultsiteRedirect />} />
      <Route path="/news" element={<Layout><News /></Layout>} />
      <Route path="/news/:slug" element={<Layout><NewsPost /></Layout>} />
      <Route path="/directory" element={<Layout><Directory /></Layout>} />
      <Route path="/historical" element={<Layout><Historical /></Layout>} />
      <Route path="/historical/progress" element={<Layout><HistoricalProgress /></Layout>} />
      <Route path="/historical/region/:country" element={<Layout><HistoricalRegion /></Layout>} />
      <Route path="/historical/:slug" element={<Layout><HistoricalRecipe /></Layout>} />
      <Route path="/leaderboard" element={<Layout><Leaderboard /></Layout>} />
      <Route path="/cook/:userId" element={<Layout><CookProfile /></Layout>} />
      <Route path="/recipe-creator" element={<Layout><RecipeCreator /></Layout>} />
      <Route path="/recipes/tag/:tag" element={<Layout><TagPage /></Layout>} />
      <Route path="/cuisine" element={<Layout><CuisineHub /></Layout>} />
      <Route path="/cuisine/:slug" element={<Layout><CuisinePage /></Layout>} />
      <Route path="/tags" element={<Layout><TagsHub /></Layout>} />
      <Route path="/diet" element={<Layout><DietIndex /></Layout>} />
      <Route path="/diet/:slug" element={<Layout><DietLanding /></Layout>} />
      <Route path="/shop" element={<Layout><Shop /></Layout>} />
      <Route path="/how-to-use" element={<Layout><HowToUse /></Layout>} />
      <Route path="/faqs" element={<Layout><FAQs /></Layout>} />
      <Route path="/faqs/:slug" element={<Layout><FAQEntry /></Layout>} />
      <Route path="/kitchen-help" element={<Layout><KitchenHelp /></Layout>} />
      <Route path="/kitchen-help/:slug" element={<Layout><KitchenHelpTerm /></Layout>} />
      <Route path="/search" element={<Layout><Search /></Layout>} />
      <Route path="/conversions" element={<Layout><Conversions /></Layout>} />
      <Route path="/pricing" element={<Layout><Pricing /></Layout>} />
      <Route path="/payment-success" element={<Layout><PaymentSuccess /></Layout>} />
      <Route path="/order-hardback" element={<Layout><OrderHardback /></Layout>} />
      <Route path="/dmca" element={<Layout><DMCA /></Layout>} />

      {/* Bring Your Data importer */}
      <Route path="/import" element={<Layout><Import /></Layout>} />
      <Route path="/try" element={<Layout><TryImport /></Layout>} />
      <Route path="/help/evernote-import" element={<Layout><EvernoteImportHelp /></Layout>} />
      <Route path="/help/import" element={<Layout><ImportHelpHub /></Layout>} />
      <Route path="/help/import/:app" element={<Layout><ImportHelpApp /></Layout>} />
      <Route path="/import-from-paprika" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-mela" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-recipe-keeper" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-cookmate" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-copy-me-that" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-bigoven" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-notion" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-evernote" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-mealmaster" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-apple-notes" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-bookmarks" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-url" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/rescue-from-clove" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/rescue-from-osta" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/rescue-from-recime" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/rescue-from-pepperplate" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/rescue-from-living-cookbook" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/rescue-from-macgourmet" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-whisk" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-anylist" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-plan-to-eat" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-zestio" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-google-keep" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-obsidian" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-eat-your-books" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-cookpad" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-yummly" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-hellofresh" element={<Layout><ImportLanding /></Layout>} />
      <Route path="/import-from-substack" element={<Layout><ImportLanding /></Layout>} />

      {/* Auth */}
      <Route path="/login" element={<Layout><Login /></Layout>} />
      <Route path="/register" element={<Layout><Register /></Layout>} />
      <Route path="/forgot-password" element={<Layout><ForgotPassword /></Layout>} />
      <Route path="/reset-password" element={<Layout><ResetPassword /></Layout>} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* App (layouted) */}
      <Route path="/dashboard" element={<Layout><Dashboard /></Layout>} />
      <Route path="/dashboard/trash" element={<Layout><CookbookTrash /></Layout>} />
      <Route path="/favourites" element={<Layout><Favourites /></Layout>} />
      <Route path="/shopping" element={<Layout><ShoppingList /></Layout>} />
      <Route path="/voice" element={<Layout><VoicePage /></Layout>} />
      <Route path="/shopping/join/:token" element={<Layout><ShoppingJoin /></Layout>} />
      <Route path="/s/:token" element={<Layout><GuestShare /></Layout>} />
      <Route path="/shopping-list-help" element={<Layout><ShoppingListHelp /></Layout>} />
      <Route path="/planner" element={<Layout><MealPlanner /></Layout>} />
      <Route path="/profile" element={<Layout><Profile /></Layout>} />
      <Route path="/activate-family" element={<Layout><ActivateFamily /></Layout>} />
      <Route path="/family-invite/:token" element={<Layout><FamilyEmailInvite /></Layout>} />
      <Route path="/scan" element={<Layout><ScanRecipe /></Layout>} />
      <Route path="/r/:id" element={<Layout><RecipeView /></Layout>} />
      <Route path="/r/:id/edit" element={<Layout><RecipeEdit /></Layout>} />
      <Route path="/cb/:id" element={<Layout><CookbookView /></Layout>} />
      <Route path="/cb/:id/edit" element={<Layout><CookbookEdit /></Layout>} />
      <Route path="/admin" element={<Layout><Admin /></Layout>} />
      <Route path="/redeem/:code" element={<Layout><RedeemGift /></Layout>} />
      <Route path="/terms" element={<Layout><Terms /></Layout>} />
      <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
      <Route path="/share-import" element={<Layout><ShareImport /></Layout>} />
      <Route path="/ios-share-setup" element={<Layout><IOSShareSetup /></Layout>} />

      {/* Fullscreen (no layout) */}
      <Route path="/sandbox" element={<OCRSandbox />} />
      <Route path="/k/:id" element={<KitchenView />} />
      <Route path="/plan/:token" element={<SharedMealPlan />} />
    </Routes>
    {/* PWA install prompt — globally rendered; hidden on Kitchen View to keep the cooking surface clean */}
    {!location.pathname.startsWith("/k/") && <InstallPrompt />}
    {/* Cookie consent — hidden on Kitchen View for the same reason */}
    {!location.pathname.startsWith("/k/") && <CookieBanner />}
    {/* Voice search FAB — self-gates on admin flag + user prefs + /k/ route */}
    <VoiceSearchFab />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <Toaster richColors position="top-center" />
          <AuthProvider>
            <SiteFlagsProvider>
              <FavouritesProvider>
              <VoiceSearchProvider>
                <AppRouter />
              </VoiceSearchProvider>
              </FavouritesProvider>
            </SiteFlagsProvider>
          </AuthProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
